import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Text, Icon, Radio, Input, Modal } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import mcpApi, {
  MASKED_SECRET,
  MCPOAuthParams,
  MCPServer,
  MCPSharingMode,
  MCPTransport,
} from '../../services/mcpApi';
import { FormState, HeaderRow, extractErrorCode, extractErrorDetail } from './types';

/** Platform identity of a server: the lowercased URL hostname (mirrors the
 * backend's MCPServerService._host_of). */
const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return '';
  }
};

// Mirrors the shared Input component's md/default variant (see components/Input/Input.tsx)
// so these fields match the ones in Create Project. Raw elements are kept here for the
// <select> and the custom-width key/value rows, which the Input component doesn't cover.
const inputClassName =
  'w-full px-bg-v2-lg py-bg-v2-md text-bg-v2-md font-bg-v2-sans font-bg-v2-regular ' +
  'border-2 border-border-secondary rounded-bg-v2-md bg-bg-primary text-text-primary ' +
  'placeholder:text-text-placeholder transition-colors duration-200 ' +
  'focus:outline-none focus:border-border-primary-hover ' +
  'disabled:bg-bg-disabled disabled:text-text-disabled disabled:border-border-disabled disabled:cursor-not-allowed';

// Keep in sync with MCP_DESCRIPTION_MAX on the backend request models.
const DESCRIPTION_MAX = 600;

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text variant="text-xs" weight="medium" color="secondary" className="mb-[var(--spacing-xs,4px)]">
    {children}
  </Text>
);

interface ServerFormViewProps {
  projectId: string;
  server?: MCPServer;
  /** The project's current servers — used to warn about same-platform siblings
   * (e.g. adding an individual server next to an existing shared one). */
  existingServers: MCPServer[];
  isBuilding: boolean;
  onSaved: (saved: MCPServer) => Promise<void>;
  onCancel: () => void;
}

/** Inspector-style add/edit form (create custom server or edit an existing one). */
export const ServerFormView: React.FC<ServerFormViewProps> = ({
  projectId,
  server,
  existingServers,
  isBuilding,
  onSaved,
  onCancel,
}) => {
  const isEdit = !!server;
  // Saving an edit invalidates the pooled runtime client — block it mid-build.
  const saveLocked = isEdit && isBuilding;

  const initialHeaders = (): HeaderRow[] => {
    if (server) {
      return Object.keys(server.custom_headers || {}).map((key) => ({
        key,
        value: MASKED_SECRET,
      }));
    }
    return [];
  };

  // OAuth was detected server-side; on edit the client credentials are editable.
  const showOAuthClient = isEdit && !!server?.oauth;

  const [form, setForm] = useState<FormState>({
    name: server?.name || '',
    description: server?.description || '',
    transport: server?.transport || 'streamable_http',
    url: server?.url || '',
    headers: initialHeaders(),
    // client_id is echoed back; the secret is write-only so it starts blank.
    oauthClientId: server?.oauth?.client_id || '',
    oauthClientSecret: '',
    oauthScopes: (server?.oauth?.scopes || []).join(' '),
    sharingMode: server?.sharing_mode || 'shared',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Backend answered 409: a same-(platform, mode) server already exists. Holds
  // the backend's message for the confirm-replace dialog.
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  // Name of the same-platform sibling of the OTHER sharing mode, if the user is
  // adding a server next to one — drives the individual-takes-priority notice.
  const [priorityNoticeFor, setPriorityNoticeFor] = useState<string | null>(null);

  /** The project's existing server of the given mode on the same platform
   * (URL host) as the form's URL, if any. */
  const siblingOfMode = (mode: MCPSharingMode): MCPServer | undefined => {
    const host = hostOf(form.url.trim());
    if (!host) return undefined;
    return existingServers.find((s) => s.sharing_mode === mode && hostOf(s.url) === host);
  };

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setHeader = (index: number, patch: Partial<HeaderRow>) => {
    setForm((prev) => ({
      ...prev,
      headers: prev.headers.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const buildHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {};
    for (const row of form.headers) {
      const key = row.key.trim();
      if (key && row.value !== '') {
        headers[key] = row.value;
      }
    }
    return headers;
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return 'Name is required';
    if (!form.url.trim()) return 'URL is required';
    if (!/^https?:\/\//i.test(form.url.trim())) return 'URL must start with http:// or https://';
    // Servers saved before the limit existed can hold a longer description — the
    // field loads it in full and the user shortens it, rather than us trimming
    // their text silently. Mirrors MCP_DESCRIPTION_MAX on the request models.
    if (form.description.length > DESCRIPTION_MAX)
      return `Description must be ${DESCRIPTION_MAX} characters or less — currently ${form.description.length}. Please shorten it.`;
    return null;
  };

  const performSave = async (replace: boolean) => {
    try {
      setFormError(null);
      setSaving(true);
      // Whether OAuth applies is detected server-side at save time — the form
      // only ever edits the CLIENT credentials of a server already detected as
      // OAuth. Scopes are passed through unchanged so an untouched form is a
      // no-op for the stored client.
      let oauth: MCPOAuthParams | undefined;
      if (showOAuthClient && server?.oauth) {
        // Blank scopes = request everything the server advertises (auto-discovered
        // on Sign in); an explicit list narrows the request to least-privilege.
        oauth = {
          client_id: form.oauthClientId.trim() || null,
          scopes: form.oauthScopes.split(/[\s,]+/).filter(Boolean),
        };
        if (form.oauthClientSecret) oauth.client_secret = form.oauthClientSecret;
      }
      let saved: MCPServer;
      if (isEdit && server) {
        // The URL is immutable after creation and intentionally not sent.
        saved = await mcpApi.updateServer(projectId, server.id, {
          name: form.name.trim(),
          description: form.description,
          transport: form.transport,
          custom_headers: buildHeaders(),
          ...(oauth ? { oauth } : {}),
        });
      } else {
        saved = await mcpApi.createServer(projectId, {
          name: form.name.trim(),
          description: form.description,
          transport: form.transport,
          url: form.url.trim(),
          custom_headers: buildHeaders(),
          sharing_mode: form.sharingMode,
          ...(replace ? { replace: true } : {}),
        });
      }
      // Immediately connect (Test Connection) so the user doesn't have to do it
      // manually. Best-effort: the test persists status/tools/last_error
      // server-side, so a connection failure here must not fail the save — the
      // list refresh in onSaved() surfaces the resulting status.
      try {
        await mcpApi.testServer(projectId, saved.id);
      } catch {
        // Swallow: status is already persisted and shown after the refresh.
      }
      await onSaved(saved);
    } catch (e: any) {
      if (!isEdit && extractErrorCode(e) === 'mcp_server_conflict') {
        // A same-(platform, mode) server exists — ask before replacing it.
        let message = extractErrorDetail(
          e,
          'An MCP server for this platform already exists in this project.'
        );
        // A sibling of the other sharing mode also exists: fold the priority
        // note into the replace dialog rather than showing two popups.
        const otherMode: MCPSharingMode = form.sharingMode === 'individual' ? 'shared' : 'individual';
        const sibling = siblingOfMode(otherMode);
        if (sibling) {
          message +=
            form.sharingMode === 'individual'
              ? ` Your individual configuration will take priority over the shared server ('${sibling.name}') for you.`
              : ` Note that the individual server ('${sibling.name}') will still take priority over this shared one for every member who has connected it.`;
        }
        setConflictMessage(message);
      } else {
        setFormError(extractErrorDetail(e, 'Failed to save MCP server'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    // Skip the notice when a same-mode sibling exists: the create 409s and the
    // replace dialog carries the priority note, so the user gets one dialog.
    if (!isEdit && !siblingOfMode(form.sharingMode)) {
      const otherSibling = siblingOfMode(form.sharingMode === 'individual' ? 'shared' : 'individual');
      if (otherSibling) {
        setPriorityNoticeFor(otherSibling.name);
        return;
      }
    }
    await performSave(false);
  };

  return (
    <div className="flex flex-col gap-[var(--spacing-lg,12px)] w-full">
      <div>
        <FieldLabel>Name</FieldLabel>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder="e.g. Figma"
          className={inputClassName}
          autoFocus={!isEdit}
        />
      </div>

      <div>
        <FieldLabel>Description (optional)</FieldLabel>
        <input
          type="text"
          value={form.description}
          onChange={(e) => setField('description', e.target.value)}
          placeholder="What this server provides"
          className={inputClassName}
          maxLength={DESCRIPTION_MAX}
        />
        <Text
          variant="text-xs"
          color={form.description.length > DESCRIPTION_MAX ? 'error' : 'tertiary'}
          className="mt-[var(--spacing-xs,4px)]"
        >
          {form.description.length}/{DESCRIPTION_MAX}
        </Text>
      </div>

      <div>
        <FieldLabel>Transport</FieldLabel>
        <select
          value={form.transport}
          onChange={(e) => setField('transport', e.target.value as MCPTransport)}
          className={inputClassName}
        >
          <option value="streamable_http">Streamable HTTP</option>
          <option value="sse">SSE (fallback)</option>
        </select>
      </div>

      <div>
        <FieldLabel>URL</FieldLabel>
        <input
          type="text"
          value={form.url}
          onChange={(e) => setField('url', e.target.value)}
          placeholder="https://your-server.example.com/mcp"
          className={`${inputClassName} font-mono`}
          disabled={isEdit}
          title={isEdit ? 'The URL cannot be changed after creation' : undefined}
        />
        {isEdit && (
          <Text variant="text-xs" color="tertiary" className="mt-[var(--spacing-xs,4px)]">
            The URL can&apos;t be changed after creation — add a new server to replace this one.
          </Text>
        )}
      </div>

      {/* Credential sharing — chosen once at creation and permanent afterwards.
          Mirrors the Owner/Collaborator billing radios in ManageCollaborationModal. */}
      {!isEdit && (
        <div>
          <FieldLabel>Credential sharing</FieldLabel>
          <div className="flex gap-bg-v2-lg items-start">
            <div
              className="flex gap-bg-v2-md items-center"
              title="All project members share the same stored credentials."
            >
              <Radio
                name="sharingMode"
                checked={form.sharingMode === 'shared'}
                onChange={() => setField('sharingMode', 'shared')}
                size="md"
              />
              <Text variant="text-sm" weight="medium" color="primary" className="font-bg-v2-sans">
                Shared
              </Text>
            </div>
            <div
              className="flex gap-bg-v2-md items-center"
              title="Everyone sees this server, but each user signs in / enters their own credentials."
            >
              <Radio
                name="sharingMode"
                checked={form.sharingMode === 'individual'}
                onChange={() => setField('sharingMode', 'individual')}
                size="md"
              />
              <Text variant="text-sm" weight="medium" color="primary" className="font-bg-v2-sans">
                Individual
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* Custom headers editor */}
      <div className="flex flex-col gap-[var(--spacing-xs,4px)]">
        <div className="flex items-center justify-between">
          <FieldLabel>Custom headers</FieldLabel>
          <button
            onClick={() => setForm((prev) => ({ ...prev, headers: [...prev.headers, { key: '', value: '' }] }))}
            className="flex items-center gap-[2px] text-text-brand-primary-hover hover:opacity-80 transition-opacity"
          >
            <Icon name={IconName.Plus} size="xs" inheritColor />
            <Text variant="text-xs" weight="medium" className="!text-text-brand-primary-hover">
              Add header
            </Text>
          </button>
        </div>
        {form.headers.map((row, index) => (
          <div key={index} className="flex gap-[var(--spacing-xs,4px)] items-center">
            <input
              type="text"
              value={row.key}
              onChange={(e) => setHeader(index, { key: e.target.value })}
              placeholder="Header name"
              className={`${inputClassName} !w-[45%] font-mono`}
            />
            <input
              type="password"
              value={row.value}
              onChange={(e) => setHeader(index, { value: e.target.value })}
              onFocus={(e) => {
                // Stored secrets are write-only: clicking into a masked value
                // clears it so the user types a replacement (or restores mask on blur).
                if (row.value === MASKED_SECRET) e.target.select();
              }}
              placeholder="Value"
              className={`${inputClassName} flex-1 font-mono`}
            />
            <button
              onClick={() =>
                setForm((prev) => ({ ...prev, headers: prev.headers.filter((_, i) => i !== index) }))
              }
              className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-secondary-hover transition-colors shrink-0"
              title="Remove header"
            >
              <Icon name={IconName.Cross} size="xs" className="text-text-tertiary" inheritColor />
            </button>
          </div>
        ))}
        {isEdit && form.headers.some((row) => row.value === MASKED_SECRET) && (
          <Text variant="text-xs" color="tertiary">
            Masked values keep the stored secret; type over one to replace it.
          </Text>
        )}
      </div>

      {/* OAuth client credentials — edit-only, and only when the backend detected
          OAuth on this server. Whether OAuth applies is never a form choice. */}
      {showOAuthClient && server?.oauth && (
        <div className="flex flex-col gap-bg-v2-xs p-bg-v2-md bg-bg-secondary border border-border-secondary rounded-bg-v2-md">
          <FieldLabel>OAuth client</FieldLabel>
          <Input
            type="text"
            value={form.oauthClientId}
            onChange={(e) => setField('oauthClientId', e.target.value)}
            placeholder="Client ID (leave blank to register automatically on sign-in)"
            className="font-mono"
          />
          <Input
            type="password"
            value={form.oauthClientSecret}
            onChange={(e) => setField('oauthClientSecret', e.target.value)}
            placeholder={
              server.oauth.has_client_secret
                ? 'Client secret (leave blank to keep)'
                : 'Client secret (optional)'
            }
          />
          <Input
            type="text"
            value={form.oauthScopes}
            onChange={(e) => setField('oauthScopes', e.target.value)}
            placeholder="Scopes (space-separated) — leave blank to request all the server offers"
            className="font-mono"
          />
          <Text variant="text-xs" color="tertiary">
            Leave scopes blank and we'll request every scope the server advertises. Enter specific
            scopes to limit access. Changing the client ID, secret, or scope will sign out all users.
          </Text>
        </div>
      )}

      {formError && (
        <div className="flex items-center gap-[var(--spacing-xs,4px)] p-[var(--spacing-md,8px)] bg-bg-error-primary border border-border-error-subtle rounded-[var(--radius-sm,6px)]">
          <Icon name={IconName.Alert} size="sm" className="text-text-error-primary shrink-0" inheritColor />
          <Text variant="text-xs" color="error">
            {formError}
          </Text>
        </div>
      )}

      <div className="flex flex-col gap-[var(--spacing-xs,4px)] pt-[var(--spacing-md,8px)] border-t border-border-secondary">
        <div className="flex justify-end gap-[var(--spacing-md,8px)]">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-[var(--spacing-lg,12px)] py-[var(--spacing-md,8px)] text-sm font-medium text-text-secondary bg-bg-secondary border border-border-secondary rounded-[var(--radius-md,8px)] hover:bg-bg-secondary-hover transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || saveLocked}
            title={saveLocked ? 'Unavailable while a build is running' : undefined}
            className="px-[var(--spacing-lg,12px)] py-[var(--spacing-md,8px)] text-sm font-medium text-text-primary-on-brand bg-bg-brand-solid rounded-[var(--radius-md,8px)] hover:bg-bg-brand-solid-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-[var(--spacing-xs,4px)]"
          >
            {saving && <Icon name={IconName.Loading} size="sm" spin className="text-text-primary-on-brand" inheritColor />}
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add server'}
          </button>
        </div>
        {saveLocked && (
          <Text variant="text-xs" color="tertiary" align="right">
            A build is in progress — changes can be saved once it finishes.
          </Text>
        )}
      </div>

      {/* Confirm-replace popup: the project already has a server for this
          (platform, sharing mode) — the backend's 409 message explains what
          replacing will do (config reset; individual mode also signs everyone
          out). Portaled to <body> for the same reason as the client-credentials
          popup in ServerDetailsView: the slide-over panel animates with a CSS
          transform, which would otherwise trap this fixed overlay inside it. */}
      {conflictMessage &&
        createPortal(
          <Modal
            isOpen={!!conflictMessage}
            onClose={() => !saving && setConflictMessage(null)}
            title="Replace existing MCP server?"
            size="sm"
            onCancel={() => setConflictMessage(null)}
            onSubmit={() => {
              setConflictMessage(null);
              void performSave(true);
            }}
            submitLabel={saving ? 'Replacing…' : 'Replace'}
            submitDisabled={saving}
            submitLoading={saving}
          >
            <Text variant="text-xs" color="secondary">
              {conflictMessage}
            </Text>
          </Modal>,
          document.body
        )}

      {/* Individual-takes-priority notice, shown before adding a server next to
          a same-platform sibling of the other sharing mode. */}
      {priorityNoticeFor &&
        createPortal(
          <Modal
            isOpen={!!priorityNoticeFor}
            onClose={() => !saving && setPriorityNoticeFor(null)}
            title={
              form.sharingMode === 'individual'
                ? 'Your configuration will take priority'
                : 'Individual configurations will take priority'
            }
            size="sm"
            onCancel={() => setPriorityNoticeFor(null)}
            onSubmit={() => {
              setPriorityNoticeFor(null);
              void performSave(false);
            }}
            submitLabel={saving ? 'Adding…' : 'Add server'}
            submitDisabled={saving}
            submitLoading={saving}
          >
            <Text variant="text-xs" color="secondary">
              {form.sharingMode === 'individual' ? (
                <>
                  This project already has a shared server (&apos;{priorityNoticeFor}&apos;) for
                  this platform. Once you add this individual configuration, it will take priority
                  for you — the agent will connect with your personal credentials instead of the
                  shared ones. The shared server is only used as a fallback while your individual
                  one isn&apos;t connected.
                </>
              ) : (
                <>
                  This project already has an individual server (&apos;{priorityNoticeFor}&apos;)
                  for this platform. You can still add this shared configuration, but individual
                  configurations take priority — any member who has connected the individual server
                  (including you) will keep using their personal credentials. This shared server is
                  only used by members whose individual configuration isn&apos;t connected.
                </>
              )}
            </Text>
          </Modal>,
          document.body
        )}
    </div>
  );
};

export default ServerFormView;
